import React, { useCallback } from "react";

import { AnalyticsTracker } from "@/features/analytics";
import useRecordUserGoal, { GoalType } from "./useRecordUserGoal";

import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import { useCurrentUser } from "@/features/auth";
import {
  PlayerSearchWatchlistMetaFragment,
  usePlayerSearchCreateWatchlistMutation,
} from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";
import { promptRef } from "@/utils/promptRef";

/**
 *
 */
interface Props {
  onCreate?: (watchlist: PlayerSearchWatchlistMetaFragment) => void;
  promptRefForModal?: React.RefObject<CustomPromptRefProps>;
}

const useCreateWatchlistActions = ({
  onCreate,
  promptRefForModal,
}: Props = {}) => {
  const recordCreateWatchlist = useRecordUserGoal({
    goalType: GoalType.CreateWatchlist,
    value: "",
  });
  const currentUser = useCurrentUser();
  const [createWatchlist, { loading }] = usePlayerSearchCreateWatchlistMutation(
    {
      onCompleted: data => {
        recordCreateWatchlist({
          value: data.createWatchlist.id,
          goalType: GoalType.CreateWatchlist,
        });
        AnalyticsTracker.user.logWatchlistCreated({
          user_id: currentUser?.id || "",
          organization_id: currentUser?.organizationId || "",
          phone_number: currentUser?.phoneNumber || "",
          watchlist_id: data.createWatchlist.id,
          watchlist_name: data.createWatchlist.name,
          action: "created",
          platform: ifWeb("web", "mobile"),
          timestamp: new Date().toISOString(),
        });
      },
      // Refetch the watchlist lists in the background so the new watchlist
      // appears, but do NOT block the UI (and the onCreate callback) on those
      // refetches completing — awaitRefetchQueries was making every create wait
      // on a slow round-trip. The mutation result already returns the created
      // watchlist, so callers get it immediately.
      refetchQueries: [
        "WatchlistListMyUserWatchlists",
        "ScoutingListUserWatchlists",
      ],
      notifyOnNetworkStatusChange: true,
    }
  );

  /** Execute the mutation and forward the result to the onCreate callback */
  const executeCreate = useCallback(
    (name: string) =>
      createWatchlist({ variables: { input: { name } } }).then(result => {
        const created = result.data?.createWatchlist;
        if (onCreate && created) {
          onCreate(created);
        }
      }),
    [createWatchlist, onCreate]
  );

  const handleAddWatchlistPress = useCallback(() => {
    return new Promise<void>(resolve => {
      const prompt = promptRefForModal ?? promptRef;
      prompt.current?.prompt(
        "New Watchlist",
        "Enter a name for this watchlist.",
        name => executeCreate(name).then(resolve)
      );
    });
  }, [executeCreate, promptRefForModal]);

  return { handleAddWatchlistPress, loading };
};

export default useCreateWatchlistActions;
